import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  ArrowUpRight,
  ShieldCheck,
  Sparkles,
  Wallet,
  LineChart,
  Building2,
  CircleDot,
} from "lucide-react";
import StoreBadges from "@/components/brand/StoreBadges";
import Seo from "@/components/seo/Seo";
import { useLocalePath } from "@/i18n/LanguageSync";

export default function Home() {
  const navigate = useNavigate();
  const { t } = useTranslation(["home", "common"]);
  const lp = useLocalePath();
  return (
    <>
      <Seo
        title={t("home:eyebrow", { defaultValue: "Modern money infrastructure" })}
        description={t("common:meta.defaultDescription")}
        path="/"
        preloadImages={[
          "https://customer-assets-lqy194kg.emergentagent.net/job_unified-cash-1/artifacts/o7smugbl_Screenshot%201.webp",
          "https://customer-assets-lqy194kg.emergentagent.net/job_unified-cash-1/artifacts/ikby14f6_Screenshot%202.webp",
        ]}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "FinancialService",
            "name": "AtlasForge Financial",
            "url": "https://atlasforgefinancial.com/",
            "logo": "https://atlasforgefinancial.com/logo512.png",
            "areaServed": "US",
            "sameAs": [
              "https://www.linkedin.com/company/atlasforgefinancial",
              "https://www.instagram.com/atlasforgefin/",
              "https://www.facebook.com/atlasforgefinancial",
              "https://www.tiktok.com/@safe.to.spend.365",
            ],
          },
        ]}
      />
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 af-soft-glow pointer-events-none" />
        <div className="absolute inset-x-0 top-0 h-[640px] af-grid-bg opacity-[0.55] [mask-image:linear-gradient(180deg,#000_30%,transparent_100%)]" />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-20 pb-28 md:pt-28 md:pb-36">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <span
                data-testid="hero-eyebrow"
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-[12px] font-medium text-slate-600 shadow-sm"
              >
                <CircleDot className="h-3.5 w-3.5 text-[#00C48C]" />
                {t("home:eyebrow")}
              </span>

              <h1
                data-testid="hero-title"
                className="font-display mt-6 text-[44px] sm:text-[56px] lg:text-[72px] leading-[1.02] tracking-tight text-[#0A1128]"
              >
                {t("home:heroTitleLine1")}
                <br />
                <span className="relative inline-block">
                  {t("home:heroTitleLine2")}
                  <span className="absolute -bottom-1 left-0 right-0 h-[6px] bg-[#FF5A36]/30 rounded-full" />
                </span>{" "}
                {t("home:heroTitleLine3")}
              </h1>

              <p
                data-testid="hero-subtitle"
                className="mt-6 text-[17px] md:text-[18px] leading-relaxed text-slate-600 max-w-2xl"
              >
                {t("home:heroBody")}
              </p>

              <div className="mt-9 flex flex-wrap items-center gap-4">
                <Link
                  to={lp("/safe-to-spend")}
                  data-testid="hero-cta-safe-to-spend"
                  className="inline-flex items-center gap-2 rounded-full bg-[#0A1128] text-white text-[14px] font-medium px-5 py-3 hover:bg-[#1E293B] transition-colors"
                >
                  {t("home:ctaDownloadStS")}
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to={lp("/ember")}
                  data-testid="hero-cta-ember"
                  className="inline-flex items-center gap-2 rounded-full bg-[#FF5A36] text-white text-[14px] font-medium px-5 py-3 hover:bg-[#E03C1A] transition-colors shadow-[0_8px_24px_-8px_rgba(255,90,54,0.5)]"
                >
                  {t("home:ctaDownloadEmber")}
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <Link
                  to={lp("/platform")}
                  data-testid="hero-cta-apis"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white text-[#0A1128] text-[14px] font-medium px-5 py-3 hover:border-slate-400 transition-colors"
                >
                  {t("home:ctaExploreApis")}
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>

              <div className="mt-10 flex items-center gap-6 text-[13px] text-slate-500">
                <span className="inline-flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-[#0A1128]" />
                  {t("common:bullets.bankGradeSecurity")}
                </span>
                <span className="hidden sm:inline-flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[#0A1128]" />
                  {t("common:bullets.aiInsights")}
                </span>
                <span className="hidden md:inline-flex items-center gap-2">
                  <Building2 className="h-4 w-4 text-[#0A1128]" />
                  {t("common:bullets.builtForBanks")}
                </span>
              </div>
            </div>

            {/* Hero visual: stacked product cards */}
            <div className="lg:col-span-5 relative">
              <div className="relative h-[520px]">
                {/* Safe to Spend 365 — real Home screenshot */}
                <div
                  data-testid="home-hero-sts"
                  className="absolute right-0 top-0 w-[210px] rotate-[3deg]"
                >
                  <span className="absolute -top-3 left-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 shadow-sm text-[10.5px] font-semibold text-[#00A373] px-2.5 py-0.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#00C48C]" />
                    Safe to Spend 365
                  </span>
                  <div className="rounded-[26px] bg-gradient-to-br from-[#0A1128] to-[#1E293B] p-1 shadow-2xl ring-1 ring-black/10">
                    <img
                      src="https://customer-assets-lqy194kg.emergentagent.net/job_unified-cash-1/artifacts/o7smugbl_Screenshot%201.webp"
                      alt="Safe to Spend 365 Home screen"
                      fetchPriority="high"
                      decoding="async"
                      width="210"
                      height="455"
                      className="rounded-[22px] w-full block"
                    />
                  </div>
                </div>

                {/* Ember360 — real Home screenshot */}
                <div
                  data-testid="home-hero-ember"
                  className="absolute left-0 top-[150px] w-[210px] -rotate-[2deg]"
                >
                  <span className="absolute -top-3 left-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-[#0A1128] border border-white/10 shadow-lg text-[10.5px] font-semibold text-[#FF8E73] px-2.5 py-0.5">
                    <span className="h-1.5 w-1.5 rounded-full bg-[#FF5A36]" />
                    Ember360
                  </span>
                  <div className="rounded-[26px] bg-gradient-to-br from-white/10 to-white/[0.02] p-1 shadow-2xl ring-1 ring-white/10 backdrop-blur">
                    <img
                      src="https://customer-assets-lqy194kg.emergentagent.net/job_unified-cash-1/artifacts/ikby14f6_Screenshot%202.webp"
                      alt="Ember360 Home screen"
                      fetchPriority="high"
                      decoding="async"
                      width="210"
                      height="455"
                      className="rounded-[22px] w-full block"
                    />
                  </div>
                </div>

                <div className="absolute right-2 bottom-0 w-[290px] rounded-2xl border border-slate-200 bg-white p-5 af-shadow-card rotate-[2deg]">
                  <span className="inline-flex items-center gap-2 text-[12px] font-semibold text-[#2563EB]">
                    <span className="h-2 w-2 rounded-full bg-[#2563EB]" />
                    APIs
                  </span>
                  <pre className="font-mono mt-3 text-[11px] leading-snug text-slate-700 bg-slate-50 rounded-lg p-3 overflow-hidden">
{`POST /v1/accounts/link
200 OK · 142 ms
{
  "account_id": "acc_a1B2",
  "status": "verified"
}`}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <section className="border-y border-slate-200/70 bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-8">
          <div className="flex flex-wrap items-center justify-between gap-6 text-[12px] uppercase tracking-[0.2em] text-slate-400">
            <span>{t("home:trustBarEyebrow")}</span>
            <span className="font-display text-[18px] tracking-tight text-slate-500 normal-case">{t("home:trustBar.communityBanks")}</span>
            <span className="font-display text-[18px] tracking-tight text-slate-500 normal-case">{t("home:trustBar.creditUnions")}</span>
            <span className="font-display text-[18px] tracking-tight text-slate-500 normal-case">{t("home:trustBar.fintechBuilders")}</span>
            <span className="font-display text-[18px] tracking-tight text-slate-500 normal-case">{t("home:trustBar.wealthPlatforms")}</span>
            <span className="font-display text-[18px] tracking-tight text-slate-500 normal-case">{t("home:trustBar.embeddedFinance")}</span>
          </div>
        </div>
      </section>

      {/* Product trio (bento) */}
      <section className="relative">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-32">
          <div className="max-w-3xl">
            <p className="text-[12px] uppercase tracking-[0.18em] text-slate-500">{t("home:productsEyebrow")}</p>
            <h2 className="font-display mt-3 text-[36px] md:text-[46px] leading-[1.05] text-[#0A1128]">
              {t("home:productsTitle")}
            </h2>
          </div>

          <div className="mt-14 grid lg:grid-cols-12 gap-6">
            {/* Safe to Spend 365 */}
            <div
              role="link"
              tabIndex={0}
              onClick={(e) => {
                if (e.target.closest("a")) return;
                navigate("/safe-to-spend");
              }}
              onKeyDown={(e) => e.key === "Enter" && navigate("/safe-to-spend")}
              data-testid="card-safe-to-spend"
              className="cursor-pointer group relative lg:col-span-7 rounded-3xl border border-slate-200 bg-white p-10 overflow-hidden hover:-translate-y-1 transition-transform af-shadow-card"
            >
              <div className="absolute -right-16 -top-16 h-72 w-72 rounded-full bg-[#00C48C]/10 blur-2xl" />
              <span className="inline-flex items-center gap-2 text-[12px] font-semibold text-[#00A373]">
                <span className="h-2 w-2 rounded-full bg-[#00C48C]" /> SAFE TO SPEND · LIVE
              </span>
              <h3 className="font-display mt-5 text-[34px] leading-[1.05]">
                Know what you can spend — with confidence.
              </h3>
              <p className="mt-4 text-[15px] text-slate-600 max-w-xl">
                A financial wellness platform that calculates your real-time
                Safe to Spend 365 amount after bills, savings goals, and upcoming
                obligations.
              </p>

              <div className="mt-8 grid sm:grid-cols-3 gap-4 max-w-xl">
                {[
                  { icon: Wallet, label: "Real-time spend" },
                  { icon: LineChart, label: "Cash flow forecast" },
                  { icon: ShieldCheck, label: "Plaid-secured" },
                ].map(({ icon: Icon, label }) => (
                  <div
                    key={label}
                    className="rounded-xl border border-slate-100 bg-[#F7FCF9] p-3.5 flex items-center gap-2.5"
                  >
                    <Icon className="h-4 w-4 text-[#00A373]" />
                    <span className="text-[12px] font-medium text-slate-700">{label}</span>
                  </div>
                ))}
              </div>

              <div className="mt-10">
                <StoreBadges product="home-safe-to-spend" />
              </div>

              <span className="absolute right-6 top-6 inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#0A1128] text-white opacity-0 group-hover:opacity-100 transition">
                <ArrowUpRight className="h-4 w-4" />
              </span>
            </div>

            {/* Ember360 */}
            <div
              role="link"
              tabIndex={0}
              onClick={(e) => {
                if (e.target.closest("a")) return;
                navigate("/ember");
              }}
              onKeyDown={(e) => e.key === "Enter" && navigate("/ember")}
              data-testid="card-ember"
              className="cursor-pointer group relative lg:col-span-5 rounded-3xl bg-[#0A1128] p-10 overflow-hidden hover:-translate-y-1 transition-transform af-shadow-card text-white"
            >
              <div className="absolute -right-12 -bottom-16 h-72 w-72 rounded-full bg-[#FF5A36]/30 blur-3xl" />
              <span className="inline-flex items-center gap-2 text-[12px] font-semibold text-[#FF8E73]">
                <span className="h-2 w-2 rounded-full bg-[#FF5A36]" /> EMBER360 · LIVE
              </span>
              <h3 className="font-display mt-5 text-[34px] leading-[1.05]">
                Your money, your portfolio, all in one place.
              </h3>
              <p className="mt-4 text-[15px] text-slate-300 max-w-md">
                The AI-powered universal money app. Aggregate banking, track
                portfolios, monitor subscriptions, and get smart cash-flow
                forecasts in one seamless view.
              </p>

              <div className="mt-8 grid grid-cols-2 gap-3">
                <div className="rounded-xl bg-white/5 p-3.5 border border-white/10">
                  <p className="text-[11px] text-slate-400 uppercase tracking-wider">Net worth</p>
                  <p className="font-display text-[20px] mt-1">$24,801</p>
                </div>
                <div className="rounded-xl bg-white/5 p-3.5 border border-white/10">
                  <p className="text-[11px] text-slate-400 uppercase tracking-wider">Portfolio</p>
                  <p className="font-display text-[20px] mt-1 text-[#00C48C]">+4.18%</p>
                </div>
              </div>

              <div className="mt-10">
                <StoreBadges product="home-ember" />
              </div>
            </div>

            {/* APIs */}
            <Link
              to="/platform"
              data-testid="card-apis"
              className="group relative lg:col-span-12 rounded-3xl border border-slate-200 bg-white p-10 overflow-hidden hover:-translate-y-1 transition-transform af-shadow-card"
            >
              <div className="grid lg:grid-cols-2 gap-10 items-center">
                <div>
                  <span className="inline-flex items-center gap-2 text-[12px] font-semibold text-[#2563EB]">
                    <span className="h-2 w-2 rounded-full bg-[#2563EB]" /> APIs FOR BANKS & FINTECHS
                  </span>
                  <h3 className="font-display mt-5 text-[34px] leading-[1.05]">
                    Modern financial infrastructure for what you build next.
                  </h3>
                  <p className="mt-4 text-[15px] text-slate-600 max-w-xl">
                    Account connectivity, payments, money movement, analytics,
                    and embedded finance — exposed as a clean, RESTful platform.
                  </p>
                  <div className="mt-6 inline-flex items-center gap-2 text-[14px] font-medium text-[#0A1128] group-hover:gap-3 transition-all">
                    Explore the platform <ArrowUpRight className="h-4 w-4" />
                  </div>
                </div>

                <div>
                  <pre className="af-code text-[12px]">
{`curl https://atlasforgefinancial.com/v1/accounts \\
  -H "Authorization: Bearer $ATLAS_KEY"

# response
{
  "data": [
    { "id": "acc_a1B2", "institution": "chase",
      "type": "checking", "balance": 4218.50 },
    { "id": "acc_c3D4", "institution": "vanguard",
      "type": "brokerage", "balance": 184220.12 }
  ]
}`}
                  </pre>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Stats / Numbers */}
      <section className="bg-[#0A1128] text-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-28">
          <div className="grid md:grid-cols-12 gap-12 items-end">
            <div className="md:col-span-5">
              <p className="text-[12px] uppercase tracking-[0.18em] text-slate-400">By the numbers</p>
              <h2 className="font-display mt-3 text-[36px] md:text-[44px] leading-[1.05]">
                Built for scale, designed for trust.
              </h2>
              <p className="mt-4 text-slate-400 max-w-md text-[15px]">
                Our platform powers millions of secure, real-time financial
                interactions every month.
              </p>
            </div>
            <div className="md:col-span-7 grid grid-cols-2 sm:grid-cols-3 gap-6">
              {[
                { v: "99.98%", l: "Platform uptime, 90d" },
                { v: "184ms", l: "p95 API latency" },
                { v: "12k+", l: "Requests / second" },
                { v: "11k+", l: "Connected institutions" },
                { v: "3", l: "Active production regions" },
                { v: "SOC 2", l: "Type II controls" },
              ].map((s) => (
                <div key={s.l} className="border-l border-white/10 pl-5">
                  <p className="font-display text-[34px] tracking-tight">{s.v}</p>
                  <p className="text-[12px] text-slate-400 mt-1">{s.l}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-24 md:py-32">
          <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-white to-[#F7F9FC] p-10 md:p-16 relative overflow-hidden">
            <div className="absolute -right-20 -top-20 h-72 w-72 rounded-full bg-[#FF5A36]/10 blur-3xl" />
            <div className="absolute -left-20 -bottom-20 h-72 w-72 rounded-full bg-[#00C48C]/10 blur-3xl" />
            <div className="relative grid md:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="font-display text-[34px] md:text-[44px] leading-[1.05] text-[#0A1128]">
                  Start with the app. <br />
                  Build with the platform.
                </h2>
                <p className="mt-4 text-slate-600 max-w-md">
                  Download Safe to Spend 365 or Ember360 today, or explore APIs to
                  power your next product.
                </p>
              </div>
              <div className="flex flex-col gap-3">
                <Link
                  to="/safe-to-spend"
                  data-testid="home-cta-sts-link"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white border border-slate-300 text-[#0A1128] text-[14px] font-medium px-5 py-3 hover:border-slate-400 transition-colors w-fit"
                >
                  Get Safe to Spend 365
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/ember"
                  data-testid="home-cta-ember-link"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white border border-slate-300 text-[#0A1128] text-[14px] font-medium px-5 py-3 hover:border-slate-400 transition-colors w-fit"
                >
                  Get Ember360
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
                <Link
                  to="/platform"
                  data-testid="home-cta-apis-link"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0A1128] text-white text-[14px] font-medium px-5 py-3 hover:bg-[#1E293B] transition-colors w-fit"
                >
                  Explore APIs for Banks & Fintechs
                  <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
